// Lightweight stale-while-revalidate cache backed by localStorage. A
// dashboard reads its cached snapshot first (shown immediately, no
// "Loading…" flash on repeat visits), then fetches fresh data in the
// background and overwrites both the screen and the cache. Not meant for
// anything sensitive or large — just list/summary views that are slow to
// warm up.

const PREFIX = 'ls_ops_cache:'

// How long a cached entry counts as "fresh enough" — inside this window a
// dashboard skips the network call on mount entirely. Without this, tab
// hopping (leave the module, come back) always re-fetches on remount even
// though the data from 2 seconds ago is still shown — this is what made
// switching tabs feel like it "reloads every time."
export const STALE_MS = 30_000

interface CacheEntry<T> {
  data: T
  timestamp: number
}

export function readCache<T>(key: string): CacheEntry<T> | null {
  try {
    const raw = localStorage.getItem(PREFIX + key)
    return raw ? (JSON.parse(raw) as CacheEntry<T>) : null
  } catch {
    return null
  }
}

export function writeCache<T>(key: string, data: T): void {
  try {
    localStorage.setItem(PREFIX + key, JSON.stringify({ data, timestamp: Date.now() }))
  } catch {
    // localStorage full or unavailable — caching is a nice-to-have, fail silently
  }
}

export function isFresh(timestamp: number, maxAgeMs: number = STALE_MS): boolean {
  return Date.now() - timestamp < maxAgeMs
}

export function formatAge(timestamp: number): string {
  const seconds = Math.floor((Date.now() - timestamp) / 1000)
  if (seconds < 10) return 'just now'
  if (seconds < 60) return `${seconds}s ago`
  const minutes = Math.floor(seconds / 60)
  if (minutes < 60) return `${minutes}m ago`
  const hours = Math.floor(minutes / 60)
  return `${hours}h ago`
}

// The cache is not keyed by person. When a different person signs in on the same browser (a shared
// front-desk computer, or a director after the Head of Operations), drop everything cached so nobody
// sees another person's snapshot — a director's view must never show other centres, even for a second.
const OWNER_KEY = 'ls_ops_cache_owner'

export function bindCacheToUser(email: string): void {
  try {
    if (localStorage.getItem(OWNER_KEY) === email) return
    Object.keys(localStorage)
      .filter((k) => k.startsWith(PREFIX))
      .forEach((k) => localStorage.removeItem(k))
    localStorage.setItem(OWNER_KEY, email)
  } catch {
    // storage unavailable — nothing cached either
  }
}
